import React, { useEffect, useState, useMemo } from 'react';
import { getLowStock, getProductSales } from '../../services/analytics.service';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer
} from 'recharts';

const ITEMS_PER_PAGE = 10;

/* ------------------------------------
   Spinner Overlay (Reusable)
------------------------------------ */
const ChartLoader = () => (
  <div className="absolute inset-0 flex items-center justify-center bg-white/70 z-10">
    <div className="h-10 w-10 rounded-full border-4 border-gray-200 border-t-blue-500 animate-spin" />
  </div>
);
const ChartEmptyState = ({ message }) => (
  <div className="absolute inset-0 flex items-center justify-center text-gray-500 z-10">
    <p className="text-sm text-center">{message}</p>
  </div>
);

const InventoryDashboard = ({ filters }) => {
  const [lowStock, setLowStock] = useState([]);
  const [productSales, setProductSales] = useState([]);
  const [loadingChart, setLoadingChart] = useState(false);
  const [loadingTable, setLoadingTable] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  /* ------------------------------------
     Fetch Data (Parallel)
  ------------------------------------ */
  useEffect(() => {
  const controller = new AbortController();

  const fetchLowStock = async () => {
    try {
      setLoadingTable(true);

      const lowStockRes = await getLowStock(controller.signal);

      setLowStock(lowStockRes || []);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Low stock load failed', err);
    } finally {
      if (!controller.signal.aborted) {
        setLoadingTable(false);
      }
    }
  };

  fetchLowStock();

  return () => {
    controller.abort();
  };
}, []);

useEffect(() => {
  if (!filters?.from || !filters?.to) return;

  const controller = new AbortController();

  const fetchProductSales = async () => {
    try {
      setLoadingChart(true);

      const productSalesRes = await getProductSales(
        {
          from: filters.from,
          to: filters.to
        },
        controller.signal
      );

      if (controller.signal.aborted) return;

      setProductSales(productSalesRes || []);
    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error('Product sales load failed', err);
    } finally {
      if (!controller.signal.aborted) {
        setLoadingChart(false);
      }
    }
  };

  fetchProductSales();

  return () => {
    controller.abort();
  };
}, [filters?.from, filters?.to]);

  /* ------------------------------------
     Filtered & Paginated Low Stock Items
  ------------------------------------ */
  const filteredItems = useMemo(
    () => lowStock.filter(item =>
      (item.ProductName || '').toLowerCase().includes(searchQuery.toLowerCase())
    ),
    [lowStock, searchQuery]
  );

  const totalPages = Math.ceil(filteredItems.length / ITEMS_PER_PAGE);
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredItems.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredItems, currentPage]);
  const hasProductSalesData = useMemo(() => {
  return Array.isArray(productSales) &&
    productSales.some(d => (d.Revenue || 0) > 0 || (d.UnitsSold || 0) > 0);
}, [productSales]);


  return (
    <div className="inventory-dashboard space-y-8">

      {/* ==============================
         Product Sales Chart
      ============================== */}
      <div className="relative">
  <h2 className="text-xl font-semibold mb-4">📊 Product Sales by Company</h2>
  <div className="bg-white p-6 rounded-xl shadow relative mb-8" style={{ height: 350 }}>
    {loadingChart && <ChartLoader />}

    {!loadingChart && !hasProductSalesData && (
      <ChartEmptyState message="No product sales data available for this period" />
    )}


    <ResponsiveContainer width="100%" height={300}>
      <BarChart
        data={productSales}
        margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
        barCategoryGap="15%"  // a bit tighter spacing
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="Company" hide />
        <YAxis />
        <Tooltip formatter={(v) => Number(v || 0).toLocaleString()} />
        <Bar dataKey="Revenue" fill="#4f46e5" name="Revenue" barSize={50} />
        <Bar dataKey="UnitsSold" fill="#6366f1" name="Units Sold" barSize={50} />
      </BarChart>
    </ResponsiveContainer>
  </div>
</div>


      {/* ==============================
         Low Stock Alerts Table
      ============================== */}
      <div className="card bg-white shadow-lg rounded-xl overflow-hidden">
        <div className="bg-gradient-to-r from-purple-500 to-pink-500 px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-bold text-white flex items-center gap-2">
            <span>📦</span> Low Stock Inventory
          </h3>
          <div className="relative">
            <input
              type="text"
              placeholder="Search products..."
              className="bg-white bg-opacity-20 border border-white border-opacity-30 rounded-lg px-4 py-2 text-white placeholder-white placeholder-opacity-70 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50 transition-all"
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-white text-opacity-70">🔍</span>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-gradient-to-r from-slate-50 to-slate-100 border-b-2 border-slate-200">
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Product</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Current</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Warning</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-slate-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {loadingTable ? (
                <tr>
                  <td colSpan={4} className="py-6 text-center text-gray-400">
                    Loading low stock items…
                  </td>
                </tr>
              ) : paginatedItems.length === 0 ? (
                <tr>
                  <td colSpan={4} className="py-12 text-center text-slate-500">
                    All products are sufficiently stocked.
                  </td>
                </tr>
              ) : (
                paginatedItems.map(item => {
                  const isLow = item.QuantityLeft < item.WarningLevelQuantity;
                  return (
                    <tr
                      key={item.ProductId}
                      className={`border-b border-slate-100 transition-colors duration-150 ${
                        isLow
                          ? 'bg-gradient-to-r from-red-50 to-pink-50 hover:from-red-100 hover:to-pink-100'
                          : 'bg-gradient-to-r from-green-50 to-emerald-50 hover:from-green-100 hover:to-emerald-100'
                      }`}
                    >
                      <td className="px-6 py-4 font-medium text-slate-800">{item.ProductName}</td>
                      <td className={`px-6 py-4 font-bold ${isLow ? 'text-red-600' : 'text-green-600'}`}>
                        {item.QuantityLeft}
                      </td>
                      <td className="px-6 py-4 text-slate-600">{item.WarningLevelQuantity}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            isLow ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'
                          }`}
                        >
                          {isLow ? '⚠️ Low' : '✓ OK'}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex justify-center items-center py-4 gap-3 bg-gradient-to-r from-slate-50 to-slate-100">
            <button
              className="px-4 py-2 bg-white border border-slate-300 rounded-lg font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 hover:border-slate-400 transition-all shadow-sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              ← Prev
            </button>
            <span className="px-4 py-2 bg-white border border-slate-300 rounded-lg font-medium text-slate-700 shadow-sm">
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="px-4 py-2 bg-white border border-slate-300 rounded-lg font-medium text-slate-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50 hover:border-slate-400 transition-all shadow-sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryDashboard;
